import React, { useState, useEffect, useRef } from 'react'
import { Send, Bot, User, Globe, Clock, CheckCircle, XCircle, AlertCircle, Settings, RefreshCw, Download, Home } from 'lucide-react'
import { io } from 'socket.io-client'
import clsx from 'clsx'
import TaskCard from './components/TaskCard'
import MessageBubble from './components/MessageBubble'
import StatusIndicator from './components/StatusIndicator'
import Downloads from './components/Downloads'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-cluster.prod.crawler.ai.rockspoon.io/proxy-pilot/v1'
const WEBSOCKET_URL = import.meta.env.WEBSOCKET_URL || 'https://api-cluster.prod.crawler.ai.rockspoon.io'
const FIXED_USER_ID = 'shared-user'

function App() {
  const [currentView, setCurrentView] = useState('chat') // 'chat' | 'downloads'
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [socket, setSocket] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [activeTasks, setActiveTasks] = useState(new Map())
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [lastError, setLastError] = useState(null)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    loadActiveTasks()
  }, [])

  const loadActiveTasks = async () => {
    setIsLoadingTasks(true)
    setLastError(null)
    
    try {
      console.log('Loading active tasks...')
      const response = await fetch(`${API_BASE_URL}/tasks?status=pending,processing`)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const tasks = await response.json()
      console.log('Received tasks:', tasks)
      
      const taskMap = new Map()
      
      if (Array.isArray(tasks)) {
        tasks.forEach(task => {
          if (task && task.task_id) {
            taskMap.set(task.task_id, task)
          }
        })
      }
      
      setActiveTasks(taskMap)
      console.log(`Loaded ${tasks.length} active tasks`)
      
    } catch (error) {
      console.error('Error loading active tasks:', error)
      setLastError(`Failed to load tasks: ${error.message}`)
    } finally {
      setIsLoadingTasks(false)
    }
  }

  // Initialize WebSocket connection
  useEffect(() => {
    let reconnectTimeout

    const connectWebSocket = () => {
      console.log('Connecting to WebSocket...')
      setConnectionStatus('connecting')
      
      const newSocket = io(WEBSOCKET_URL, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        timeout: 20000,
        forceNew: true,
        path: '/proxy-pilot/v1/socket.io/'
      })

      newSocket.on('connect', () => {
        console.log('Connected to WebSocket server')
        setConnectionStatus('connected')
        setLastError(null)
        
        activeTasks.forEach((task, taskId) => {
          console.log('Subscribing to task:', taskId)
          newSocket.emit('subscribe_task', { task_id: taskId })
        })
      })

      newSocket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error)
        setConnectionStatus('disconnected')
        setLastError(`Connection failed: ${error.message}`)
        
        // Reconnect after delay
        reconnectTimeout = setTimeout(() => {
          console.log('Attempting to reconnect...')
          connectWebSocket()
        }, 5000)
      })

      newSocket.on('disconnect', (reason) => {
        console.log('Disconnected from WebSocket server:', reason)
        setConnectionStatus('disconnected')
        
        // Auto-reconnect unless it was a manual disconnect
        if (reason === 'io server disconnect') {
          reconnectTimeout = setTimeout(() => {
            console.log('Attempting to reconnect after server disconnect...')
            connectWebSocket()
          }, 3000)
        }
      })

      newSocket.on('task_update', (data) => {
        console.log('Task update received:', data)
        if (data && data.task_id) {
          setActiveTasks(prev => {
            const updated = new Map(prev)
            const existingTask = updated.get(data.task_id) || {}
            updated.set(data.task_id, { ...existingTask, ...data })
            return updated
          })
        }
      })

      newSocket.on('task_status', (data) => {
        console.log('Task status received:', data)
        if (data && data.task_id) {
          setActiveTasks(prev => {
            const updated = new Map(prev)
            const existingTask = updated.get(data.task_id) || {}
            updated.set(data.task_id, { ...existingTask, ...data })
            return updated
          })
        }
      })

      newSocket.on('subscribed', (data) => {
        console.log('Subscribed to task:', data)
      })

      newSocket.on('connected', (data) => {
        console.log('WebSocket connected:', data)
      })

      newSocket.on('error', (error) => {
        console.error('WebSocket error:', error)
        setLastError(`WebSocket error: ${error.message || error}`)
      })

      setSocket(newSocket)
    }

    connectWebSocket()

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      if (socket) {
        socket.close()
      }
    }
  }, [])

  useEffect(() => {
    if (socket && socket.connected) {
      activeTasks.forEach((task, taskId) => {
        console.log('Auto-subscribing to task:', taskId)
        socket.emit('subscribe_task', { task_id: taskId })
      })
    }
  }, [socket, activeTasks])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading) return

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)
    setLastError(null)

    try {
      const response = await fetch(`${API_BASE_URL}/crawl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: userMessage.content,
          user_id: FIXED_USER_ID
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const data = await response.json()
      console.log('Task created:', data)
      
      // Subscribe to task updates
      if (socket && socket.connected && data.task_id) {
        console.log('Subscribing to new task:', data.task_id)
        socket.emit('subscribe_task', { task_id: data.task_id })
        
        // Initialize task in active tasks
        setActiveTasks(prev => {
          const updated = new Map(prev)
          updated.set(data.task_id, {
            task_id: data.task_id,
            status: data.status,
            message: data.message,
            query: userMessage.content,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: FIXED_USER_ID
          })
          return updated
        })
      }

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.message,
        timestamp: new Date(),
        taskId: data.task_id,
        status: data.status,
      }

      setMessages(prev => [...prev, botMessage])

    } catch (error) {
      console.error('Error sending message:', error)
      setLastError(`Failed to send message: ${error.message}`)
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: `Sorry, there was an error processing your request: ${error.message}`,
        timestamp: new Date(),
        error: true,
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-success-500'
      case 'connecting': return 'text-warning-500'
      default: return 'text-error-500'
    }
  }

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected'
      case 'connecting': return 'Connecting...'
      default: return 'Disconnected'
    }
  }

  // Navigation component
  const Navigation = () => (
    <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
      <button
        onClick={() => setCurrentView('chat')}
        className={clsx(
          'flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
          currentView === 'chat'
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        )}
      >
        <Home className="w-4 h-4" />
        <span>Chat</span>
      </button>
      <button
        onClick={() => setCurrentView('downloads')}
        className={clsx(
          'flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
          currentView === 'downloads'
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        )}
      >
        <Download className="w-4 h-4" />
        <span>Downloads</span>
      </button>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-primary-500 rounded-lg p-2">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Proxy Pilot</h1>
                <p className="text-sm text-gray-500">Places Crawler Assistant</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Navigation />
              <StatusIndicator 
                status={connectionStatus}
                label={getConnectionStatusText()}
              />
              <div className="text-sm text-gray-500">
                {activeTasks.size} active task{activeTasks.size !== 1 ? 's' : ''}
              </div>
              <button
                onClick={loadActiveTasks}
                disabled={isLoadingTasks}
                className={clsx(
                  "flex items-center space-x-1 text-sm px-3 py-1 rounded",
                  isLoadingTasks
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                )}
              >
                <RefreshCw className={clsx("w-4 h-4", isLoadingTasks && "animate-spin")} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
          
          {/* Error banner */}
          {lastError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {lastError}
              <button 
                onClick={() => setLastError(null)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Navigation */}
      <nav className="max-w-4xl mx-auto px-4 py-2">
        <Navigation />
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {currentView === 'chat' ? (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Messages Area */}
              <div className="h-96 overflow-y-auto scrollbar-thin px-6 py-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-lg mb-2">Welcome to Proxy Pilot!</p>
                    <p className="text-gray-400 text-sm">
                      Ask me to find places, extract data, or crawl websites. 
                      <br />
                      Try: "Find phone numbers of Asian restaurants in Paris"
                    </p>
                  </div>
                )}
                
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    taskData={message.taskId ? activeTasks.get(message.taskId) : null}
                  />
                ))}
                
                {isLoading && (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                    <span className="text-sm">Processing your request...</span>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Active Tasks Panel */}
              {activeTasks.size > 0 && (
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Active Tasks ({activeTasks.size})
                  </h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
                    {Array.from(activeTasks.values()).map((task) => (
                      <TaskCard key={task.task_id} task={task} />
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="border-t border-gray-200 px-6 py-4">
                <form onSubmit={handleSubmit} className="flex space-x-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Ask me to find places or extract data..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || !inputValue.trim()}
                    className={clsx(
                      'px-6 py-3 rounded-lg font-medium transition-all flex items-center space-x-2',
                      isLoading || !inputValue.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                    )}
                  >
                    <Send className="w-4 h-4" />
                    <span>Send</span>
                  </button>
                </form>
              </div>
            </div>

            {/* Stats - show only in chat view */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-success-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Completed Tasks</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {Array.from(activeTasks.values()).filter(t => t.status === 'completed').length}
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-warning-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Processing</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {Array.from(activeTasks.values()).filter(t => t.status === 'processing').length}
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <XCircle className="w-5 h-5 text-error-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Failed</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {Array.from(activeTasks.values()).filter(t => t.status === 'failed').length}
                </div>
              </div>
            </div>
          </>
        ) : (
          <Downloads />
        )}
      </main>
    </div>
  )
}

export default App